import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Card, 
  Form, 
  Button, 
  Row, 
  Col, 
  Dropdown, 
  Spinner, 
  Alert,
  ProgressBar,
  Tab,
  Nav,
  Modal
} from 'react-bootstrap';
import PracticeQuestionSession from './PracticeQuestionSession';

const StyleAdaptiveMode = ({ 
  models, 
  selectedModel, 
  onModelSelect, 
  apiBaseUrl,
  selectedFireworkModel,
  setSelectedFireworkModel,
  styleData,
  setStyleData,
  sessionActive,
  setSessionActive
}) => {
  const [studyMaterials, setStudyMaterials] = useState([]);
  const [previousExams, setPreviousExams] = useState([]);
  const [questionCount, setQuestionCount] = useState(5);
  const [difficulty, setDifficulty] = useState('medium');
  const [professorName, setProfessorName] = useState('');
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fireworkModels, setFireworkModels] = useState([]);
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [answersVisible, setAnswersVisible] = useState([]);

  // Extract styleAnalysis and questions from styleData if it exists
  const styleAnalysis = styleData?.style_analysis || null;
  const questions = styleData?.questions || null;

  // Initialize answer visibility array when questions change
  useEffect(() => {
    if (questions && questions.length > 0) {
      // Initialize with all answers hidden
      setAnswersVisible(new Array(questions.length).fill(false));
    }
  }, [questions]);

  // Function to toggle visibility for a specific question
  const toggleAnswer = (index) => {
    const newVisibility = [...answersVisible];
    newVisibility[index] = !newVisibility[index];
    setAnswersVisible(newVisibility);
  };

  // Fetch Firework models when models are loaded
  useEffect(() => {
    const fetchFireworkModels = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/models`);
        if (response.data.firework_models) {
          const fireworkModelsList = Object.entries(response.data.firework_models).map(([id, name]) => ({
            id,
            name
          }));
          setFireworkModels(fireworkModelsList);
        }
      } catch (err) {
        console.error('Error fetching Firework models:', err);
      }
    };
    
    if (models.length > 0) {
      fetchFireworkModels();
    }
  }, [models, apiBaseUrl]);

  const handleStudyMaterialsChange = (e) => {
    setStudyMaterials(Array.from(e.target.files));
  };

  const handlePreviousExamsChange = (e) => {
    setPreviousExams(Array.from(e.target.files));
  };

  const handleQuestionCountChange = (e) => {
    setQuestionCount(parseInt(e.target.value));
  };

  const handleDifficultySelect = (level) => {
    setDifficulty(level);
  };

  const handleFireworkModelSelect = (modelId) => {
    if (setSelectedFireworkModel) {
      setSelectedFireworkModel(modelId);
    }
  };

  const handleSubjectChange = (e) => {
    setSubject(e.target.value);
  };

  const handleProfessorNameChange = (e) => {
    setProfessorName(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (studyMaterials.length === 0) {
      setError('Please upload at least one study material file');
      return;
    }
    
    if (previousExams.length === 0) {
      setError('Please upload at least one previous exam paper');
      return;
    }
    
    if (!subject.trim()) {
      setError('Please enter the subject or course name');
      return;
    }
    
    if (!selectedModel) {
      setError('Please select an AI model');
      return;
    }
    
    setLoading(true);
    setError('');
    setStyleData(null);
    setSessionActive(false);
    
    const formData = new FormData();
    studyMaterials.forEach(file => {
      formData.append('study_materials', file);
    });
    previousExams.forEach(file => {
      formData.append('previous_exams', file);
    });
    formData.append('question_count', questionCount);
    formData.append('ai_provider', selectedModel);
    formData.append('difficulty', difficulty);
    formData.append('subject', subject);
    if (professorName.trim()) {
      formData.append('professor_name', professorName);
    }
    
    if (selectedModel === 'firework' && selectedFireworkModel) {
      formData.append('firework_model', selectedFireworkModel);
    }
    
    try {
      await axios.get(`${apiBaseUrl}/health`);
      
      const totalStudyMaterialSizeMB = studyMaterials.reduce((total, file) => total + file.size, 0) / (1024 * 1024);
      const totalExamSizeMB = previousExams.reduce((total, file) => total + file.size, 0) / (1024 * 1024);
      const totalSizeMB = totalStudyMaterialSizeMB + totalExamSizeMB;
      const timeout = 900000;
      
      if (totalSizeMB > 5) {
        setError(`Processing ${Math.round(totalSizeMB)}MB of material, this may take several minutes. Please wait...`);
      }
      
      const response = await axios.post(`${apiBaseUrl}/generate-style-adaptive-questions`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: timeout
      });
      
      if (response.data) {
        // Store the complete response in styleData
        setStyleData(response.data);
        setError('');
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      
      if (err.response) {
        const serverMessage = err.response.data?.detail || JSON.stringify(err.response.data) || err.message;
        setError(`Server error (${err.response.status}): ${serverMessage}`);
      } else if (err.request) {
        if (err.code === 'ECONNABORTED') {
          setError(`Request timed out. Processing takes longer than expected. Try with fewer or smaller files.`);
        } else {
          setError('No response from server. Please check if the backend service is running.');
        }
      } else {
        setError(`Error: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartPractice = () => {
    setSessionActive(true);
  };

  const handleEndSession = () => {
    setSessionActive(false);
  };

  const handleBackToForm = () => {
    // Force a page reload to reset everything
    window.location.reload();
  };
  if (sessionActive && questions) {
    return (
      <PracticeQuestionSession 
        questions={questions} 
        onEndSession={handleEndSession}
        selectedModel={selectedModel}
        selectedFireworkModel={selectedFireworkModel}
        apiBaseUrl={apiBaseUrl}
      />
    );
  }

  return (
    <>
      {/* Show the form only if questions have not been generated or we're not in session */}
      {!styleData && (
        <Card>
          <Card.Body>
            <Card.Title className="mb-4">Style Adaptive Question Generation</Card.Title>
            <p className="mb-4">
              This mode analyzes your professor's question style from previous exams and generates custom questions 
              from your study materials that match their style.
            </p>
            
            {error && <Alert variant={error.includes('Processing') ? "info" : "danger"}>{error}</Alert>}
            
            <Form onSubmit={handleSubmit}>
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group controlId="studyMaterials" className="mb-3">
                    <Form.Label><strong>Upload Study Materials</strong></Form.Label>
                    <Form.Control 
                      type="file" 
                      multiple 
                      onChange={handleStudyMaterialsChange} 
                      accept=".pdf,.docx,.doc,.txt"
                    />
                    <Form.Text className="text-muted">
                      Upload lecture slides, notes, or content you want to be tested on
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group controlId="previousExams" className="mb-3">
                    <Form.Label><strong>Upload Previous Exams</strong></Form.Label>
                    <Form.Control 
                      type="file" 
                      multiple 
                      onChange={handlePreviousExamsChange} 
                      accept=".pdf,.docx,.doc,.txt"
                    />
                    <Form.Text className="text-muted">
                      Upload previous exams from the same professor to analyze their style
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group controlId="subject" className="mb-3">
                    <Form.Label><strong>Subject / Course Name</strong></Form.Label>
                    <Form.Control 
                      type="text" 
                      value={subject}
                      onChange={handleSubjectChange}
                      placeholder="e.g., Computer Science, Organic Chemistry, etc."
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group controlId="professorName" className="mb-3">
                    <Form.Label><strong>Professor Name</strong> (Optional)</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={professorName}
                      onChange={handleProfessorNameChange}
                      placeholder="e.g., Dr. Smith"
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group controlId="aiModel">
                    <Form.Label><strong>Select AI Model</strong></Form.Label>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-primary" id="dropdown-model" className="w-100 text-start">
                        {models.find(m => m.id === selectedModel)?.name || 'Select AI Model'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="w-100">
                        {models.map(model => (
                          <Dropdown.Item 
                            key={model.id} 
                            onClick={() => onModelSelect(model.id)}
                            active={selectedModel === model.id}
                          >
                            {model.name}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group controlId="difficulty">
                    <Form.Label><strong>Base Difficulty Level</strong></Form.Label>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary" id="dropdown-difficulty" className="w-100 text-start">
                        {difficulty === 'easy' ? 'Easy' : difficulty === 'medium' ? 'Medium' : 'Hard'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="w-100">
                        <Dropdown.Item 
                          onClick={() => handleDifficultySelect('easy')}
                          active={difficulty === 'easy'}
                        >
                          Easy
                        </Dropdown.Item>
                        <Dropdown.Item 
                          onClick={() => handleDifficultySelect('medium')}
                          active={difficulty === 'medium'}
                        >
                          Medium
                        </Dropdown.Item>
                        <Dropdown.Item 
                          onClick={() => handleDifficultySelect('hard')}
                          active={difficulty === 'hard'}
                        >
                          Hard
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                    <Form.Text className="text-muted">
                      The actual difficulty will be adapted to match the professor's style
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              {selectedModel === 'firework' && fireworkModels.length > 0 && (
                <Row className="mb-4">
                  <Col md={12}>
                    <Form.Group controlId="fireworkModel">
                      <Form.Label><strong>Select OpenSource AI Model</strong></Form.Label>
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" id="dropdown-firework-model" className="w-100 text-start">
                          {fireworkModels.find(m => m.id === selectedFireworkModel)?.name || 'Select AI Model'}
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="w-100">
                          {fireworkModels.map(model => (
                            <Dropdown.Item 
                              key={model.id} 
                              onClick={() => handleFireworkModelSelect(model.id)}
                              active={selectedFireworkModel === model.id}
                            >
                              {model.name}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </Form.Group>
                  </Col>
                </Row>
              )}
              
              <Row className="mb-4">
                <Col md={12}>
                  <Form.Group controlId="questionCount">
                    <Form.Label><strong>Number of Questions: {questionCount}</strong></Form.Label>
                    <Form.Range 
                      min={3} 
                      max={15} 
                      step={1} 
                      value={questionCount} 
                      onChange={handleQuestionCountChange} 
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <div className="d-grid gap-2">
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={loading || studyMaterials.length === 0 || previousExams.length === 0 || !subject.trim()}
                  size="lg"
                >
                  {loading ? 
                    <><Spinner animation="border" size="sm" /> Analyzing Professor Style & Generating Questions...</> : 
                    'Generate Style-Adaptive Questions'}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}

      {styleData && questions && (
        <Card className="mt-4">
          <Card.Body>
            <Card.Title>Generated Questions</Card.Title>
            <Button 
              variant="outline-primary" 
              className="mb-4"
              onClick={() => setShowStyleModal(true)}
              disabled={!styleAnalysis}
            >
              View Professor Style Analysis
            </Button>
            
            {questions.map((q, index) => (
              <div key={index} className="mb-5 p-3 border rounded">
                <h5>Question {index + 1}</h5>
                <p><strong>Question:</strong> {q.question}</p>
                
                {/* Toggle button for each individual question */}
                <Button 
                  variant={answersVisible[index] ? "outline-secondary" : "primary"} 
                  onClick={() => toggleAnswer(index)}
                  className="mb-1"
                >
                  {answersVisible[index] ? "Hide Answer" : "Show Answer"}
                </Button>
                
                {/* Conditionally render the answer based on this question's visibility */}
                {answersVisible[index] && q.answer && (
                  <div className="answer-container p-1 bg-light rounded mt-1">
                    <p><strong>Answer:</strong> {q.answer}</p>
                    {q.suggestions && (
                      <p><strong>Suggestions:</strong> {q.suggestions}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
            
            <div className="text-center">
              <Button 
                variant="primary" 
                onClick={handleStartPractice}
                className="mt-3"
              >
                Start Practice Session
              </Button>
              <Button 
                variant="outline-secondary" 
                onClick={handleBackToForm}
                className="mt-3 ms-3"
              >
                Back to Form
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      <Modal 
        show={showStyleModal} 
        onHide={() => setShowStyleModal(false)} 
        size="xl"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Professor Style Analysis for {subject}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {professorName && <div className="text-muted mb-3">Professor: {professorName}</div>}
          
          <Tab.Container defaultActiveKey="question-patterns">
            <Row>
              <Col sm={3}>
                <Nav variant="pills" className="flex-column mb-3">
                  <Nav.Item>
                    <Nav.Link eventKey="question-patterns">Question Patterns</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="language-style">Language Style</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="topic-emphasis">Topic Emphasis</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="difficulty-distribution">Difficulty Distribution</Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col sm={9}>
                <Tab.Content>
                  <Tab.Pane eventKey="question-patterns">
                    <h5 className="mb-3">Common Question Patterns</h5>
                    <ul className="list-group">
                      {styleAnalysis?.question_patterns?.map((pattern, index) => (
                        <li key={index} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-top">
                            <div>
                              <strong>{pattern.pattern_type}</strong>
                              <p className="mb-0">{pattern.description}</p>
                            </div>
                            <span className="badge bg-primary rounded-pill ms-2">
                              {pattern.frequency ? `${Math.round(pattern.frequency * 100)}%` : ''}
                            </span>
                          </div>
                          {pattern.examples && pattern.examples.length > 0 && (
                            <div className="mt-2">
                              <small className="text-muted">Example: "{pattern.examples[0]}"</small>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </Tab.Pane>
                  
                  <Tab.Pane eventKey="language-style">
                    <h5 className="mb-3">Language and Phrasing Style</h5>
                    <Card className="bg-light mb-3">
                      <Card.Body>
                        <p>{styleAnalysis?.language_style?.overall_description || "No language style analysis available."}</p>
                        
                        {styleAnalysis?.language_style?.key_phrases && styleAnalysis.language_style.key_phrases.length > 0 && (
                          <div className="mt-3">
                            <h6>Common Phrases and Terms:</h6>
                            <div className="d-flex flex-wrap gap-2">
                              {styleAnalysis.language_style.key_phrases.map((phrase, index) => (
                                <span key={index} className="badge bg-info">{phrase}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {styleAnalysis?.language_style?.question_starters && styleAnalysis.language_style.question_starters.length > 0 && (
                          <div className="mt-3">
                            <h6>Common Question Starters:</h6>
                            <ul>
                              {styleAnalysis.language_style.question_starters.map((starter, index) => (
                                <li key={index}>{starter}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Tab.Pane>
                  
                  <Tab.Pane eventKey="topic-emphasis">
                    <h5 className="mb-3">Topic Emphasis</h5>
                    {styleAnalysis?.topic_emphasis && Object.keys(styleAnalysis.topic_emphasis).length > 0 ? (
                      <div>
                        {Object.entries(styleAnalysis.topic_emphasis)
                          .sort(([, a], [, b]) => b - a)
                          .map(([topic, emphasis], index) => (
                            <div key={index} className="mb-3">
                              <div className="d-flex justify-content-between">
                                <strong>{topic}</strong>
                                <span>{(emphasis * 100).toFixed(1)}%</span>
                              </div>
                              <ProgressBar 
                                now={emphasis * 100} 
                                variant={emphasis > 0.3 ? "primary" : emphasis > 0.15 ? "info" : "secondary"}
                              />
                            </div>
                          ))
                        }
                      </div>
                    ) : (
                      <p>No topic emphasis data available.</p>
                    )}
                  </Tab.Pane>
                  
                  <Tab.Pane eventKey="difficulty-distribution">
                    <h5 className="mb-3">Difficulty Distribution</h5>
                    {styleAnalysis?.difficulty_distribution ? (
                      <div>
                        <div className="mb-4">
                          <div className="d-flex justify-content-between mb-1">
                            <strong>Easy</strong>
                            <span>{(styleAnalysis.difficulty_distribution.easy * 100).toFixed(1)}%</span>
                          </div>
                          <ProgressBar 
                            now={styleAnalysis.difficulty_distribution.easy * 100} 
                            variant="success"
                          />
                        </div>
                        
                        <div className="mb-4">
                          <div className="d-flex justify-content-between mb-1">
                            <strong>Medium</strong>
                            <span>{(styleAnalysis.difficulty_distribution.medium * 100).toFixed(1)}%</span>
                          </div>
                          <ProgressBar 
                            now={styleAnalysis.difficulty_distribution.medium * 100} 
                            variant="info"
                          />
                        </div>
                        
                        <div className="mb-4">
                          <div className="d-flex justify-content-between mb-1">
                            <strong>Hard</strong>
                            <span>{(styleAnalysis.difficulty_distribution.hard * 100).toFixed(1)}%</span>
                          </div>
                          <ProgressBar 
                            now={styleAnalysis.difficulty_distribution.hard * 100} 
                            variant="danger"
                          />
                        </div>
                      </div>
                    ) : (
                      <p>No difficulty distribution data available.</p>
                    )}
                    
                    {styleAnalysis?.difficulty_breakdown && (
                      <div className="mt-4">
                        <h6>Detailed Breakdown:</h6>
                        <Card className="bg-light">
                          <Card.Body>
                            <p>{styleAnalysis.difficulty_breakdown}</p>
                          </Card.Body>
                        </Card>
                      </div>
                    )}
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Row>
          </Tab.Container>
          
          <div className="mt-4">
            <h5>Style Adaptation Summary</h5>
            <Card className="bg-light">
              <Card.Body>
                <p>{styleAnalysis?.adaptation_summary || "The generated questions have been adapted to match the professor's questioning style based on previous exams."}</p>
              </Card.Body>
            </Card>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStyleModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
//ok
export default StyleAdaptiveMode;